import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import ContactButtons from "@/components/ContactButtons";
import ShareCard from "@/components/ShareCard";
import { CATEGORY_ICON, TIER_COLORS, TIER_LABEL } from "@/data/brand";
import { resolvePhotoUrl } from "@/lib/photos";

export default function ListingDetail() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/listings/${id}`).then((r) => setListing(r.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-24 text-center text-zinc-400">Caricamento…</div>;
  if (!listing) return <div className="p-24 text-center text-zinc-400">Annuncio non trovato.</div>;

  const cover = resolvePhotoUrl(listing.photos?.[activePhoto]);
  const icon = CATEGORY_ICON[listing.category_slug] || "fa-briefcase";

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="listing-detail-page">
      <SEO
        title={`${listing.title} — ${listing.category_name} | HostMarket`}
        description={(listing.subtitle || listing.description || "").substring(0, 155) || `Scopri ${listing.title} su HostMarket, la piazza dei professionisti degli affitti brevi.`}
        path={`/annunci/${listing.id}`}
        image={cover || undefined}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: listing.title,
          description: listing.description,
          provider: { "@type": "Organization", name: listing.author_name },
          areaServed: listing.zone_online ? "Italia" : (listing.zone_regioni?.[0] || listing.city || "Italia"),
          image: listing.photos || [],
          category: listing.category_name,
        }}
      />

      <div className="text-xs text-zinc-500 mb-5">
        <Link to="/annunci" className="hover:text-[#FFBE00]">Annunci</Link>
        <span className="mx-2">/</span>
        <Link to={`/categoria/${listing.category_slug}`} className="hover:text-[#E619A8]">{listing.category_name}</Link>
        <span className="mx-2">/</span>
        <span className="text-zinc-400">{listing.title}</span>
      </div>

      {/* TWO-COLUMN LAYOUT */}
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-8">
        {/* LEFT COLUMN: photo + title + subtitle + description + meta */}
        <div>
          {listing.photos?.length > 0 ? (
            <div className="mb-6 animate-fade-up" data-testid="listing-gallery">
              <div className="bg-[#0A0A0A] overflow-hidden border border-zinc-800 rounded-xl relative group flex items-center justify-center" style={{ minHeight: "320px", maxHeight: "70vh" }}>
                <img
                  src={cover}
                  alt={listing.title}
                  className="w-full h-auto max-h-[70vh] object-contain"
                />
                {listing.tier && (
                  <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-black/80 border border-zinc-700 text-xs font-bold uppercase tracking-widest backdrop-blur" style={{ color: TIER_COLORS[listing.tier] }}>
                    {TIER_LABEL[listing.tier]}
                  </div>
                )}
              </div>
              {listing.photos.length > 1 && (
                <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
                  {listing.photos.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => setActivePhoto(i)}
                      className={`w-24 h-16 flex-shrink-0 overflow-hidden rounded border-2 transition-all ${i === activePhoto ? "border-[#FFBE00]" : "border-zinc-800 hover:border-zinc-600 opacity-70 hover:opacity-100"}`}
                      data-testid={`thumb-${i}`}
                    >
                      <img src={p} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="aspect-[16/10] bg-gradient-to-br from-[#111] to-[#1a1a1a] rounded-xl mb-6 flex items-center justify-center border border-zinc-800">
              <i className={`fas ${icon} text-7xl text-zinc-700`}></i>
            </div>
          )}

          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500 mb-3 animate-fade-up" style={{ animationDelay: '0.05s' }}>
            <i className={`fas ${icon} text-[#FFBE00]`}></i>
            <span>{listing.category_name}</span>
            {listing.city && <><span className="mx-2">·</span><i className="fas fa-map-marker-alt text-[#E619A8]"></i><span>{listing.city}{listing.region ? `, ${listing.region}` : ""}</span></>}
            <span className="mx-2">·</span>
            <i className="fas fa-user text-zinc-500"></i><span>{listing.unique_views || 0} visitatori</span>
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.05] animate-fade-up" style={{ animationDelay: '0.1s' }} data-testid="listing-title">{listing.title}</h1>
          {listing.subtitle && (
            <p className="mt-3 text-lg md:text-xl text-zinc-300 font-medium leading-snug animate-fade-up" style={{ animationDelay: '0.15s' }} data-testid="listing-subtitle">{listing.subtitle}</p>
          )}

          {/* WORK ZONES are now displayed in the sticky sidebar (right column) */}

          {listing.video_url && (
            <div className="mt-6 animate-fade-up" style={{ animationDelay: '0.22s' }} data-testid="listing-video">
              <div className="text-xs uppercase tracking-widest text-[#FFBE00] mb-2">· Video</div>
              <a href={listing.video_url} target="_blank" rel="noreferrer" className="hm-card flex items-center justify-between p-4 hover:border-[#E619A8]">
                <span className="truncate text-sm">{listing.video_url}</span>
                <i className="fas fa-up-right-from-square text-zinc-500"></i>
              </a>
            </div>
          )}

          <div className="hm-card p-6 mt-6 animate-fade-up" style={{ animationDelay: '0.25s' }} data-testid="listing-description">
            <div className="text-xs uppercase tracking-widest text-[#FFBE00] mb-4">· Descrizione</div>
            <p className="whitespace-pre-line text-zinc-200 leading-relaxed">{listing.description || "Nessuna descrizione fornita."}</p>
          </div>

          {(listing.cap || listing.address) && (
            <div className="hm-card p-6 mt-4 animate-fade-up" style={{ animationDelay: '0.35s' }}>
              <div className="text-xs uppercase tracking-widest text-[#E619A8] mb-4">· Sede</div>
              <div className="text-zinc-300 mb-3">
                <i className="fas fa-map-marker-alt mr-2 text-[#E619A8]"></i>
                {listing.cap ? `${listing.cap}${listing.city ? ` · ${listing.city}` : ""}` : `${listing.address}${listing.city ? ` · ${listing.city}` : ""}`}
              </div>
              <div className="aspect-video rounded-md overflow-hidden border border-zinc-800">
                <iframe
                  title="mappa"
                  width="100%"
                  height="100%"
                  loading="lazy"
                  allowFullScreen
                  src={`https://www.google.com/maps?q=${encodeURIComponent(
                    listing.cap
                      ? `${listing.cap} ${listing.city || "Italia"}`
                      : `${listing.address || ""} ${listing.city || ""}`.trim()
                  )}&output=embed`}
                />
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: sticky sidebar with contacts */}
        <aside>
          <div className="sticky top-24 space-y-4">
            <div className="hm-card p-6" data-testid="contact-card">
              <div className="text-xs uppercase tracking-widest text-[#FFBE00] mb-4">· Contatta il professionista</div>
              <ContactButtons listing={listing} />
            </div>

            {/* Compact "Dove lavora" sidebar card */}
            {(listing.zone_online || (listing.zone_regioni || []).length > 0 || (listing.zone_comuni || []).length > 0) && (
              <div className="hm-card p-5" data-testid="listing-zones-sidebar">
                <div className="text-xs uppercase tracking-widest text-[#FFBE00] mb-3 flex items-center gap-2">
                  <span className="hm-globe-spin text-[#FFBE00]"><i className="fas fa-globe"></i></span>
                  <span>Dove lavora</span>
                </div>

                {listing.zone_online && (
                  <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-gradient-to-r from-[#FFBE00]/15 to-[#E619A8]/15 border border-[#FFBE00]/30 mb-3" data-testid="zone-online-badge">
                    <span className="hm-globe-spin text-[#FFBE00] text-lg flex-shrink-0"><i className="fas fa-globe"></i></span>
                    <div className="text-xs">
                      <div className="font-bold text-white">Online in tutta Italia</div>
                      <div className="text-zinc-400 text-[11px]">Lavora a distanza ovunque</div>
                    </div>
                  </div>
                )}

                {(listing.zone_regioni || []).length > 0 && (
                  <div className="mb-3">
                    <div className="text-[10px] uppercase tracking-widest text-[#E619A8] font-bold mb-1.5">
                      Regioni · {listing.zone_regioni.length}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {listing.zone_regioni.map((r) => (
                        <span key={r} className="px-2 py-1 rounded-md bg-[#E619A8]/10 border border-[#E619A8]/30 text-[#E619A8] text-[11px] font-semibold">
                          {r}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {(listing.zone_comuni || []).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">
                      Comuni · {listing.zone_comuni.length}
                    </div>
                    <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                      {listing.zone_comuni.map((c) => (
                        <span key={c} className="px-2 py-1 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-200 text-[11px]">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Share card with QR */}
            <ShareCard
              url={typeof window !== "undefined" ? window.location.href : ""}
              title={listing.title}
            />

            <div className="hm-card p-6">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">· Dettagli</div>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-zinc-500">Categoria</dt><dd className="text-white font-medium">{listing.category_name}</dd></div>
                {listing.tier && <div className="flex justify-between"><dt className="text-zinc-500">Piano</dt><dd className="font-bold" style={{ color: TIER_COLORS[listing.tier] }}>{TIER_LABEL[listing.tier]}</dd></div>}
                {listing.city && <div className="flex justify-between"><dt className="text-zinc-500">Città</dt><dd className="text-white">{listing.city}</dd></div>}
                <div className="flex justify-between"><dt className="text-zinc-500">Visitatori</dt><dd className="text-[#FFBE00] font-bold">{listing.unique_views || 0}</dd></div>
                <div className="flex justify-between"><dt className="text-zinc-500">Autore</dt><dd className="text-white truncate">{listing.author_name}</dd></div>
              </dl>
            </div>
            <div className="hm-card p-5 text-center">
              <div className="text-xs uppercase tracking-widest text-[#E619A8] mb-2">· Hostmarket</div>
              <p className="text-xs text-zinc-400 leading-relaxed">Un prodotto dell'ecosistema <a href="https://vitadahost.it" target="_blank" rel="noreferrer" className="text-[#FFBE00] hover:underline">Vita da Host</a>.</p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
